import React, { useState } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs"; // Optimized for protection/styling
import { RxCross1 } from "react-icons/rx";
import { BASE_URL } from "utils/constant";
import SampleInventoryLibrary from "assets/sample_sheets/Sample_Inventory.library.xlsx";

const EXPORT_COLUMNS = [
  { header: "material_id (do not edit)", key: "material_id", width: 30, locked: true },
  { header: "material_name", key: "material_name", width: 30, locked: false },
  { header: "specification", key: "specification", width: 40, locked: false },
  { header: "subcategory", key: "subcategory", width: 25, locked: false },
  { header: "uom", key: "uom", width: 15, locked: false },
  { header: "brand_name", key: "brand_name", width: 25, locked: false },
];

const ORG_SUFFIXES = new Set([
  "co", "company", "corp", "inc", "limited", "llp", "ltd", "private", "pvt",
]);

const TEMPLATE_EXTRA_ROWS = 100;

const getOrganizationDetails = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}");
  } catch (error) {
    console.error("Failed to parse organization details", error);
    return {};
  }
};

const getOrgInitials = (orgName = "") => {
  const cleanedParts = orgName
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^a-zA-Z0-9]/g, ""))
    .filter(Boolean);
  const parts = cleanedParts.filter((part) => !ORG_SUFFIXES.has(part.toLowerCase()));
  const sourceParts = parts.length ? parts : cleanedParts;

  if (sourceParts.length === 0) return "OR";
  if (sourceParts.length === 1) return sourceParts[0].slice(0, 2).toUpperCase();

  return sourceParts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
};

const getMaterialSheetId = (material, orgName, index) => {
  const createdOn = new Date(material?.created_on);
  const year = Number.isNaN(createdOn.getTime())
    ? new Date().getFullYear()
    : createdOn.getFullYear();
  const rawMaterialId = material?.id ?? material?.material_id ?? index + 1;
  const numericId = String(rawMaterialId).match(/\d+/)?.[0] || String(index + 1);

  return `${getOrgInitials(orgName)}-${year}-MAT-${numericId.padStart(3, "0")}`;
};

const MaterialLibraryUploadModal = ({
  onClose,
  organizationId,
  materials = [],
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const endpoint = `${BASE_URL}/org/material-library/bulk-upload/`;

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById("material-library-file-upload");
    if (fileInput) fileInput.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setUploadMessage("Please select a file to upload");
      return;
    }
    setIsLoading(true);
    setUploadMessage("");
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (organizationId) {
        formData.append("organization", organizationId);
      }
      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access")}`,
        },
      });
      setUploadMessage("File successfully uploaded");
      setSelectedFile(null);
      setTimeout(() => onClose?.(), 1200);
      return response?.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setUploadMessage(`Error uploading file: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportExistingMaterials = async () => {
    setIsExporting(true);
    try {
      const organization = getOrganizationDetails();
      const orgName = organization?.organization_name || "Organization";

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Materials");

      worksheet.columns = EXPORT_COLUMNS.map((col) => ({
        header: col.header,
        key: col.key,
        width: col.width,
      }));

      materials.forEach((material) => {
        worksheet.addRow({
          material_id: getMaterialSheetId(material, orgName, worksheet.rowCount),
          material_name: material?.material_name || "",
          specification: material?.specification || "",
          subcategory: material?.subcategory || "",
          uom: material?.uom || "",
          brand_name: material?.brand_name || "",
        });
      });

      Array.from({ length: TEMPLATE_EXTRA_ROWS }).forEach(() => {
        const row = worksheet.addRow({
          material_id: "",
          material_name: "",
          specification: "",
          subcategory: "",
          uom: "",
          brand_name: "",
        });

        row.eachCell((cell, colNumber) => {
          if (colNumber === 1) {
            cell.protection = { locked: true };
            cell.font = { color: { argb: "FF888888" } };
          } else {
            cell.protection = { locked: false };
          }
        });
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;

        row.eachCell((cell, colNumber) => {
          if (colNumber === 1) {
            cell.protection = { locked: true };
            cell.font = { color: { argb: "FF888888" } };
          } else {
            cell.protection = { locked: false };
          }
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.protection = { locked: true };
        cell.font = { bold: true };
      });

      await worksheet.protect("", {
        selectLockedCells: false,
        selectUnlockedCells: true,
        formatCells: false,
        formatColumns: false,
        formatRows: false,
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      saveAs(blob, "existing_materials_sheet.xlsx");

    } catch (error) {
      setUploadMessage(`Export Error: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg w-[560px] p-6"
        style={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Upload materials (library)</h3>
          <RxCross1
            className="text-[#232323] w-5 h-5 cursor-pointer"
            onClick={onClose}
          />
        </div>

        {!selectedFile ? (
          <div
            className={`border-2 border-dashed rounded-lg p-8 mb-6 transition-colors ${dragActive ? "bg-purple-50 border-purple-400" : "border-gray-300"
              }`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="material-library-file-upload"
                disabled={isLoading}
              />
              <label
                htmlFor="material-library-file-upload"
                className={`flex flex-col items-center ${isLoading ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                  }`}
              >
                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <span className="text-gray-600">Click to browse files</span>
                <span className="text-xs text-gray-400 mt-2">or drag & drop file here</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="border-2 border-gray-300 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-800 truncate" title={selectedFile.name}>
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <button onClick={removeSelectedFile} className="ml-2 text-gray-500 hover:text-red-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="mb-6 flex justify-between gap-4 flex-wrap">
          {materials.length > 0 && (
            <button
              type="button"
              onClick={handleExportExistingMaterials}
              disabled={isExporting}
              className={`text-[#58267F] font-medium flex items-center ${isExporting ? "opacity-70 cursor-not-allowed" : "hover:underline"
                }`}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h16v16H4zM8 12h8m-4-4v8" />
              </svg>
              {isExporting ? "Preparing export..." : "Export Existing Materials Sheet"}
            </button>
          )}
          <a
            href={SampleInventoryLibrary}
            download
            className="text-[#58267F] font-medium flex items-center hover:underline"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Sample
          </a>
        </div>

        {uploadMessage && (
          <div className={`p-3 mb-4 rounded-lg ${uploadMessage.includes("Error") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
            {uploadMessage}
          </div>
        )}

        <button
          onClick={handleFileUpload}
          disabled={isLoading || !selectedFile}
          className={`w-full bg-[#3B0069] text-white py-3 rounded-lg hover:bg-purple-800 transition-colors flex items-center justify-center ${isLoading || !selectedFile ? "opacity-70 cursor-not-allowed" : ""
            }`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Upload File</span>
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MaterialLibraryUploadModal;